import Button from '@mui/material/Button';
import { TemplatingArea, TemplatingContextType } from '@tyro/api';
import type { CalendarParty } from '@tyro/calendar';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RHFAutocomplete,
  useFormValidator,
} from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { usePartySearchProps } from '@tyro/people';
import { getTemplateContextSearch } from '@tyro/printing';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { getResolveTemplate } from '../../api/resolve-template';

export type PrintTemplateData = {
  parties: CalendarParty[];
};

type SelectRecipientsModalProps = {
  isOpen: boolean;
  context: TemplatingContextType | undefined;
  htmlContent: string | null | undefined;
  onClose: () => void;
  templateType: string | null | undefined;
};

const templateArea = new Map<string, TemplatingArea>([
  ['letter', TemplatingArea.Letter],
  ['email', TemplatingArea.Mail],
  ['sms', TemplatingArea.Sms],
]);

export function SelectRecipientsModal({
  isOpen,
  context,
  htmlContent,
  templateType,
  onClose,
}: SelectRecipientsModalProps) {
  const { t } = useTranslation(['templates', 'common']);

  const { resolver, rules } = useFormValidator<PrintTemplateData>();
  const { control, handleSubmit, reset } = useForm<PrintTemplateData>({
    resolver: resolver({
      parties: rules.required(),
    }),
    defaultValues: {
      parties: [],
    },
  });

  const handlePreview = handleSubmit(async ({ parties }) => {
    if (context) {
      const resolvedTemplateResponse = await getResolveTemplate({
        area: templateArea.get(templateType ?? '') ?? TemplatingArea.Letter,
        context,
        template: htmlContent ?? '',
        partyIds: parties?.map((member) => member?.partyId) ?? [],
      });

      if (resolvedTemplateResponse.templating_resolveTemplate?.temporaryUrl) {
        window.open(
          resolvedTemplateResponse.templating_resolveTemplate?.temporaryUrl,
          '_blank',
          'noreferrer',
        );
        onClose();
      }
    }
  });

  const { includeSearchType } = useMemo(
    () => getTemplateContextSearch(context ?? TemplatingContextType.Student),
    [context],
  );

  const participantsProps = usePartySearchProps({
    filter: {
      includeSearchType,
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      scroll="paper"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{t('templates:preview')}</DialogTitle>
      <DialogContent sx={{ pt: 0.75 }}>
        <RHFAutocomplete<PrintTemplateData, CalendarParty, true>
          {...participantsProps}
          controlProps={{
            name: 'parties',
            control,
          }}
          limitTags={2}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="soft" onClick={onClose}>
          {t('common:actions.cancel')}
        </Button>

        <Button variant="contained" onClick={handlePreview}>
          {t('common:actions.preview')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
